import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ASSISTANT_HIGHLIGHT_EVENT, findVisibleAnchorElement } from '../lib/assistant/appActions';

/**
 * Visual "pointing finger" for the AI help assistant.
 *
 * Listens for the assistant:highlight event, finds the element tagged with the
 * matching data-assistant-anchor attribute, scrolls it into view, and draws a
 * pulsing ring + label over it. Position is tracked every frame while active
 * so the ring follows scrolling/layout shifts. Dismisses on click, navigation,
 * or after a few seconds.
 */

interface ActiveHighlight {
  /** Pre-registered anchor id (resolved to an element at highlight time)… */
  anchor?: string;
  /** …or a direct element reference (text-based highlights). */
  element?: HTMLElement;
  label: string;
}

const HIGHLIGHT_DURATION_MS = 7000;

export default function AssistantHighlightOverlay() {
  const [active, setActive] = useState<ActiveHighlight | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const onHighlight = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (!detail.anchor && !(detail.element instanceof HTMLElement)) return;
      setActive({
        anchor: detail.anchor ? String(detail.anchor) : undefined,
        element: detail.element instanceof HTMLElement ? detail.element : undefined,
        label: String(detail.label || ''),
      });
    };
    window.addEventListener(ASSISTANT_HIGHLIGHT_EVENT, onHighlight);
    return () => window.removeEventListener(ASSISTANT_HIGHLIGHT_EVENT, onHighlight);
  }, []);

  useEffect(() => {
    if (!active) return;

    const el = active.element && active.element.isConnected
      ? active.element
      : active.anchor
        ? findVisibleAnchorElement(active.anchor)
        : null;
    if (!el) {
      setActive(null);
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

    // Track the element's position every frame — it may move (scroll, layout,
    // animations) and the ring must stay glued to it.
    const track = () => {
      setRect(el.getBoundingClientRect());
      rafRef.current = requestAnimationFrame(track);
    };
    rafRef.current = requestAnimationFrame(track);

    const dismiss = () => setActive(null);
    timerRef.current = setTimeout(dismiss, HIGHLIGHT_DURATION_MS);
    // Any interaction or navigation means the user moved on
    window.addEventListener('pointerdown', dismiss, { once: true });
    window.addEventListener('app:navigate', dismiss, { once: true });
    window.addEventListener('popstate', dismiss, { once: true });

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('pointerdown', dismiss);
      window.removeEventListener('app:navigate', dismiss);
      window.removeEventListener('popstate', dismiss);
      setRect(null);
    };
  }, [active]);

  if (!active || !rect) return null;

  const pad = 6;
  const labelBelow = rect.top < 72; // label flips under the ring near the top edge

  // Portal to <body>: modals (e.g. app settings) live in their own top-level
  // stacking contexts, so rendering inside the app tree would put the ring
  // underneath them no matter the z-index.
  return createPortal(
    <div
      aria-hidden
      data-assistant-capture-ignore="true"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 2147483100 }}
    >
      <div
        className="absolute rounded-xl"
        style={{
          left: rect.left - pad,
          top: rect.top - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          border: '2.5px solid rgba(59, 130, 246, 0.95)',
          boxShadow: '0 0 0 6px rgba(59, 130, 246, 0.25), 0 0 24px 8px rgba(59, 130, 246, 0.35)',
          animation: 'assistant-highlight-pulse 1.4s ease-in-out infinite',
        }}
      />
      {active.label && (
        <div
          className="absolute px-2.5 py-1 rounded-lg text-xs font-medium text-white whitespace-nowrap"
          style={{
            left: Math.max(8, Math.min(rect.left - pad, window.innerWidth - 180)),
            top: labelBelow ? rect.bottom + pad + 8 : rect.top - pad - 30,
            background: 'rgba(37, 99, 235, 0.95)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          }}
        >
          {active.label.replace(/^the /, 'The ')}
        </div>
      )}
      <style>{`
        @keyframes assistant-highlight-pulse {
          0%, 100% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.25), 0 0 24px 8px rgba(59, 130, 246, 0.35); }
          50% { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0.12), 0 0 32px 12px rgba(59, 130, 246, 0.5); }
        }
      `}</style>
    </div>,
    document.body,
  );
}
